// src/LoginProvider.js
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { db, auth } from "./firebase";
import {
  collection, query, where, limit, getDocs,
  addDoc, setDoc, doc, serverTimestamp , getDoc , updateDoc
} from "firebase/firestore";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
  signOut , sendPasswordResetEmail
} from "firebase/auth";


import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const LoginContext = createContext(null);

export function LoginProvider({ children }) {
  auth.languageCode = "fr";
  const [user, setUser] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("auth_user") || "null"); } catch { return null; }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    try {
      if (user) sessionStorage.setItem("auth_user", JSON.stringify(user));
      else sessionStorage.removeItem("auth_user");
    } catch {}
  }, [user]);

  /* =======================
     1) LOGIN (téléphone + Firestore)
     ======================= */
  const login = async ({ phone, password }) => {
    setLoading(true); setError("");
    try {
      const q = query(
        collection(db, "ecom_users"),
        where("phone", "==", phone),
        where("password", "==", password),
        limit(1)
      );
      const snap = await getDocs(q);
      if (snap.empty) {
        setError("Numero ou mot de passe incorrect...");
        setLoading(false);
        return null;
      }
      const docSnap = snap.docs[0];
      const data = docSnap.data();
      const u = { id: docSnap.id, ...data };
      setUser(u);
      setLoading(false);
      toast.success('Connexion Reussie...Profitez!!!', { autoClose: 2000 });
      return u;
    } catch (e) {
      console.error("login error:", e);
      setError("Oups!!! Reessayer...");
      setLoading(false);
      return null;
    }
  };

  /* =======================
     2) REGISTER (téléphone + Firestore)
     ======================= */
  const register = async ({ name, phone, password }) => {
    setLoading(true); setError("");
    try {
      const existsQ = query(collection(db, "ecom_users"), where("phone", "==", phone), limit(1));
      const existsSnap = await getDocs(existsQ);
      if (!existsSnap.empty) {
        setError("Ce téléphone est déjà utilisé");
        setLoading(false);
        return null;
      }
      const ref = await addDoc(collection(db, "ecom_users"), {
        name, phone, password, // ⚠️ en clair (démo)
        createdAt: serverTimestamp(),
        provider: "phone"
      });
      const newUser = { id: ref.id, name, phone, provider: "phone" };
      setUser(newUser);
      setLoading(false);
      return newUser;
    } catch (e) {
      console.error("register error:", e);
      setError("erreur");
      setLoading(false);
      return null;
    }
  };

  /* =======================
     3) REGISTER (email + Firebase Auth)
     ======================= */
  // const registerEmail = async ({ name, email, phone, password }) => {
  //   setLoading(true); setError("");
  //   try {
  //     const cred = await createUserWithEmailAndPassword(auth, email, password);
  //     const fuser = cred.user;

  //     // nom visible dans Firebase Auth
  //     await updateProfile(fuser, { displayName: name });

  //     // (facultatif) envoyer un e-mail de vérification
  //     try { await sendEmailVerification(fuser); } catch {}

  //     // profil en Firestore sous l'UID (doc id déterministe)
  //     await setDoc(doc(db, "ecom_users", fuser.uid), {
  //       uid: fuser.uid,
  //       name,
  //       email: fuser.email,
  //       phone: phone || "",
  //       provider: "password",
  //       Etat: "actif",
  //       createdAt: serverTimestamp()
  //     }, { merge: true });

  //     const u = { id: fuser.uid, uid: fuser.uid, name, email: fuser.email, phone: phone || "", provider: "password" };
  //     setUser(u);
  //     setLoading(false);
  //     toast.success("Compte créé par e-mail !");
  //     return u;
  //   } catch (e) {
  //     console.error("registerEmail error:", e);
  //     let msg = "Inscription impossible.";
  //     if (e.code === "auth/email-already-in-use") msg = "E-mail déjà utilisé.";
  //     if (e.code === "auth/invalid-email") msg = "E-mail invalide.";
  //     if (e.code === "auth/weak-password") msg = "Mot de passe trop faible (min. 6).";
  //     setError(msg);
  //     setLoading(false);
  //     return null;
  //   }
  // };

  const registerEmail = async ({ name, email, phone, password, foreignCode }) => {
  setLoading(true); setError("");

  try {
    // 1) Normalisation du code
    const cleanForeign = (foreignCode ?? "").trim() || null;

    // 2) Vérification du code AVANT la création Auth
    if (cleanForeign) {
      const q = query(
        collection(db, "users"),
        where("PersonalCode", "==", cleanForeign),
        limit(1)
      );
      const snap = await getDocs(q);

      if (snap.empty) {
        // aucun user avec ce ForeignCode → on bloque l'inscription
        setError("Le code d'invitation saisi est invalide.");
        setLoading(false);
        return null;
      }
      // sinon : au moins un doc possède ce ForeignCode → on autorise
    }

    // 3) Création Auth
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const fuser = cred.user;

    // 4) Nom visible dans Firebase Auth
    await updateProfile(fuser, { displayName: name });

    // 5) (Facultatif) e-mail de vérification
    //try { await sendEmailVerification(fuser); } catch {}

    // 6) Profil Firestore (doc = UID) + ForeignCode (code ou null)
    await setDoc(doc(db, "users", fuser.uid), {
      uid: fuser.uid,
      name,
      email: fuser.email,
      phone: phone || "",
      Employee: false,
      provider: "password",
      Etat: "actif",
      ForeignCode: cleanForeign ?? null, // 👈 ici
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      discount: Number(0),
      PersonalCode: ""
    }, { merge: true });

    const u = {
      id: fuser.uid, uid: fuser.uid,
      name, email: fuser.email, phone: phone || "", provider: "password" , 
      ForeignCode: cleanForeign , PersonalCode: ""
    };
    setUser(u);
    setLoading(false);
    toast?.success?.("Compte créé par e-mail !");
    return u;

  } catch (e) {
    console.error("registerEmail error:", e);
    let msg = "Inscription impossible.";
    if (e.code === "auth/email-already-in-use") msg = "E-mail déjà utilisé.";
    if (e.code === "auth/invalid-email") msg = "E-mail invalide.";
    if (e.code === "auth/weak-password") msg = "Mot de passe trop faible (min. 6).";
    setError(msg);
    setLoading(false);
    return null;
  }
};

  /* =======================
     4) LOGIN (email + Firebase Auth)
     ======================= */
 const loginEmail = async ({ email, password }) => {
  setLoading(true);
  setError("");
  try {
    // 1) Auth Firebase
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const fuser = cred.user;

    // 2) Profil Firestore obligatoire: ecom_users/{uid}
    const profRef = doc(db, "users", fuser.uid);
    const profSnap = await getDoc(profRef);


    if (!profSnap.exists()) {
      await signOut(auth);
      setError("Compte non autorisé : profil introuvable.");
      setLoading(false);
      return null;
    }

    const data = profSnap.data();
    const etat = String((data.Etat ?? data.etat ?? "")).toLowerCase();

    if (etat !== "actif") {
      await signOut(auth);
      setError("Compte désactivé. Contactez le support.");
      setLoading(false);
      return null;
    }

    await updateDoc(profRef, {
      updatedAt: serverTimestamp(),
    });

    // 3) OK : on met l'utilisateur en session (données du profil en priorité)
    // const u = {
    //   id: fuser.uid,
    //   uid: fuser.uid,
    //   name: data.name ?? fuser.displayName ?? "",
    //   email: data.email ?? fuser.email ?? email,
    //   phone: data.phone ?? "",
    //   provider: "password",
    //   Etat: data.Etat ?? data.etat ?? "actif",
    //   discount: Number(data.discount) ?? Number(0),
    // };
    const u = { id: fuser.uid, ...data };
    setUser(u);
    setLoading(false);
    return u;
  } catch (e) {
    console.error("loginEmail error:", e);
    let msg = "Connexion impossible.";
    if (e.code === "auth/wrong-password") msg = "Mot de passe incorrect.";
    if (e.code === "auth/user-not-found") msg = "Utilisateur introuvable.";
    if (e.code === "auth/too-many-requests") msg = "Trop d’essais, réessaye plus tard.";
    setError(msg);
    setLoading(false);
    return null;
  }
};


  const logout = async () => {
    try { await signOut(auth); } catch {}
    setUser(null);
    setError("");
    toast.error('Vous etes deconnecter...!!!', { autoClose: 2000 });
  };

  /* =======================
+    5) MOT DE PASSE OUBLIÉ (email + Firebase Auth)
+    ======================= */
 const forgotPasswordEmail = async (email) => {
   setLoading(true); setError("");
   try {
     // Facultatif : personnaliser l'URL de redirection après reset
     const actionCodeSettings = {
       url: "https://dollarstoretogo.com", // où revenir après avoir défini un nouveau MDP
       handleCodeInApp: false,
     };
     await sendPasswordResetEmail(auth, email, actionCodeSettings);
     toast.info("E-mail de réinitialisation envoyé. Vérifie ta boîte de réception.");
     setLoading(false);
     return true;
   } catch (e) {
     console.error("forgotPasswordEmail error:", e);
     let msg = "Impossible d'envoyer l'e-mail.";
     if (e.code === "auth/invalid-email") msg = "E-mail invalide.";
     if (e.code === "auth/user-not-found") msg = "Aucun compte avec cet e-mail.";
     if (e.code === "auth/too-many-requests") msg = "Trop de tentatives. Réessaye plus tard.";
     setError(msg);
     setLoading(false);
     return false;
  }
 };

  const value = useMemo(() => ({
    user, loading, error,
    login, register,           // tes flux actuels (téléphone)
    registerEmail, loginEmail, forgotPasswordEmail , 
    logout, setError, setUser
  }), [user, loading, error]);




  return <LoginContext.Provider value={value}> <ToastContainer /> {children}</LoginContext.Provider>;
}

export function useLogin() {
  const ctx = useContext(LoginContext);
  if (!ctx) throw new Error("useLogin must be used within <LoginProvider />");
  return ctx;
}
